import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../../components/admin/AdminLayout';
import { useToast } from '../../contexts/ToastContext';
import { getAdminLogs, getAdminLogStats, clearAdminLogs } from '../../api/client';
import Button from '../../components/ui/Button';
import Skeleton from '../../components/ui/Skeleton';
import {
  AlertTriangle,
  Trash2,
  RefreshCw,
  Search,
  AlertCircle,
  Info,
  Bug,
  ShieldAlert,
  Activity,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

interface LogItem {
  id: number;
  level: string;
  message: string;
  context: any;
  source_file: string;
  source_line: number;
  user_id: number;
  ip_address: string;
  route: string;
  created_at: string;
}

const levelConfig: Record<string, { icon: React.ElementType; bg: string; text: string; border: string }> = {
  ERROR: { icon: AlertCircle, bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  EXCEPTION: { icon: Bug, bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  CRITICAL: { icon: ShieldAlert, bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  WARNING: { icon: AlertTriangle, bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  NOTICE: { icon: Info, bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  INFO: { icon: Info, bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  DEBUG: { icon: Activity, bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' },
  DEPRECATED: { icon: Calendar, bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
};

export default function AdminLogs(): React.JSX.Element {
  const toast = useToast();
  const [logs, setLogs] = useState<LogItem[]>([]);
  const [levels, setLevels] = useState<Array<{level: string; count: number}>>([]);
  const [stats, setStats] = useState({ today: 0, week: 0, errors_24h: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [clearing, setClearing] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const [res, statsRes] = await Promise.all([
        getAdminLogs({ page, per_page: perPage, level: levelFilter, search, from: fromDate, to: toDate }),
        getAdminLogStats(),
      ]);
      setLogs(Array.isArray(res.logs) ? res.logs : []);
      setLevels(Array.isArray(res.levels) ? res.levels : []);
      setTotalPages(res.pagination?.total_pages ?? 1);
      if (statsRes) {
        setStats({
          today: statsRes.today ?? 0,
          week: statsRes.week ?? 0,
          errors_24h: statsRes.errors_24h ?? 0,
        });
      }
    } catch (err: any) {
      toast.show(err.message || 'Failed to load logs', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, levelFilter, search, fromDate, toDate, toast]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleClear = async (type: 'level' | 'older_than', value: string) => {
    setClearing(true);
    try {
      const params = type === 'level' ? { level: value } : { older_than_days: Number(value) };
      await clearAdminLogs(params);
      toast.show('Logs cleared', 'success');
      fetchLogs();
    } catch (err: any) {
      toast.show(err.message || 'Failed to clear logs', 'error');
    } finally {
      setClearing(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">System Logs</h1>
            <p className="text-sm text-slate-500">Monitor errors, warnings, and system events</p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={fetchLogs} leftIcon={<RefreshCw className="w-4 h-4" />}>
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Today</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{stats.today.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">This Week</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{stats.week.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Errors (24h)</p>
            <p className={`text-2xl font-bold mt-1 ${stats.errors_24h > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {stats.errors_24h.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search logs..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              />
            </div>
            <select
              value={levelFilter}
              onChange={(e) => { setLevelFilter(e.target.value); setPage(1); }}
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20"
            >
              <option value="">All Levels</option>
              {levels.map((l) => (
                <option key={l.level} value={l.level}>{l.level} ({l.count})</option>
              ))}
            </select>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setPage(1); }}
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {['ERROR', 'EXCEPTION', 'WARNING', 'INFO'].map((lvl) => (
              <button
                key={lvl}
                onClick={() => handleClear('level', lvl)}
                disabled={clearing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-50 text-slate-600 hover:bg-red-50 hover:text-red-700 border border-slate-200 hover:border-red-200 transition-colors"
              >
                <Trash2 className="w-3 h-3" />
                Clear {lvl}
              </button>
            ))}
            <button
              onClick={() => handleClear('older_than', '30')}
              disabled={clearing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-50 text-slate-600 hover:bg-red-50 hover:text-red-700 border border-slate-200 hover:border-red-200 transition-colors"
            >
              <Trash2 className="w-3 h-3" />
              Clear older than 30 days
            </button>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3 w-32">Level</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Message</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3 w-40">Source</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3 w-32">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-5 py-4"><Skeleton className="h-5 w-16 rounded-full" /></td>
                      <td className="px-5 py-4"><Skeleton className="h-4 w-full" /></td>
                      <td className="px-5 py-4"><Skeleton className="h-4 w-24" /></td>
                      <td className="px-5 py-4"><Skeleton className="h-4 w-20" /></td>
                    </tr>
                  ))
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-12 text-center text-slate-500 text-sm">No logs found</td>
                  </tr>
                ) : (
                  logs.map((log) => {
                    const config = levelConfig[log.level] || levelConfig.DEBUG;
                    const Icon = config.icon;
                    const isExpanded = expandedId === log.id;
                    return (
                      <React.Fragment key={log.id}>
                        <tr
                          onClick={() => setExpandedId(isExpanded ? null : log.id)}
                          className="hover:bg-slate-50 transition-colors cursor-pointer"
                        >
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${config.bg} ${config.text} border ${config.border}`}>
                              <Icon className="w-3 h-3" />
                              {log.level}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <p className="text-sm text-slate-900 font-medium line-clamp-1">{log.message}</p>
                          </td>
                          <td className="px-5 py-3">
                            <p className="text-xs text-slate-500 font-mono truncate max-w-[140px]">
                              {log.source_file ? log.source_file.split('/').pop() : '—'}
                              {log.source_line ? `:${log.source_line}` : ''}
                            </p>
                          </td>
                          <td className="px-5 py-3">
                            <p className="text-xs text-slate-500">{new Date(log.created_at).toLocaleString()}</p>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={4} className="px-5 py-4 bg-slate-50/50">
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                className="space-y-3"
                              >
                                <div>
                                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Full Message</p>
                                  <p className="text-sm text-slate-900 font-mono bg-white border border-slate-200 rounded-lg p-3">{log.message}</p>
                                </div>
                                {log.context && (
                                  <div>
                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Context</p>
                                    <pre className="text-xs text-slate-700 font-mono bg-white border border-slate-200 rounded-lg p-3 overflow-x-auto">
                                      {JSON.stringify(log.context, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                                  {log.user_id && <span>User: #{log.user_id}</span>}
                                  {log.ip_address && <span>IP: {log.ip_address}</span>}
                                  {log.route && <span>Route: {log.route}</span>}
                                </div>
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && logs.length > 0 && (
            <div className="flex items-center justify-between px-5 py-4 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  leftIcon={<ChevronLeft className="w-4 h-4" />}
                >
                  Prev
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  rightIcon={<ChevronRight className="w-4 h-4" />}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
